import { gql } from '@/shared/graphql/generated/explorer';
import { type AuthorityMembershipWithEpochFragment } from '@/shared/graphql/generated/explorer/graphql';
import { type TokenCast } from '@/shared/utils/TokenAmount';

export const getValidatorsByAccountIdsQuery = gql(/* GraphQL */ `
  query getValidatorsByAccountIds($ids: [String!]!) {
    validators: allValidators(filter: { accountByAccountId: { idSs58: { in: $ids } } }) {
      nodes {
        id
      }
    }
  }
`);

export const paginatedPenaltiesByValidatorQuery = gql(/* GraphQL */ `
  query paginatedPenaltiesByValidatorQuery(
    $ids: [Int!]
    $first: Int
    $last: Int
    $after: Cursor
    $before: Cursor
  ) {
    penalties: allPenalties(
      filter: { validatorId: { in: $ids } }
      orderBy: ID_DESC
      after: $after
      before: $before
      first: $first
      last: $last
    ) {
      pageInfo {
        startCursor
        endCursor
        hasNextPage
        hasPreviousPage
      }
      edges {
        node {
          ...Penalty
        }
      }
    }
  }
`);

export const getTotalRewards = gql(/* GraphQL */ `
  query TotalValidatorRewards($idSs58: String!) {
    account: accountByIdSs58(idSs58: $idSs58) {
      validator: validatorByAccountId {
        memberships: authorityMembershipsByValidatorId {
          aggregates {
            sum {
              reward
            }
          }
        }
      }
    }
  }
`);

export const getAllEpochsQuery = gql(/* GraphQL */ `
  query getAllEpochs(
    $first: Int
    $offset: Int
    $validatorIdSs58: String
    $operatorIdSs58: String
    $withValidator: Boolean = false
    $withOperator: Boolean = false
  ) {
    epochs: allEpoches(orderBy: ID_DESC, offset: $offset, first: $first) {
      totalCount
      pageInfo {
        endCursor
        hasNextPage
        hasPreviousPage
        startCursor
      }
      edges {
        node {
          ...Epoch
          ...EpochParticipation
        }
      }
    }
  }
`);

export const getEpochByIdQuery = gql(/* GraphQL */ `
  query getEpochsById(
    $epochId: Int!
    $validatorIdSs58: String
    $operatorIdSs58: String
    $withValidator: Boolean = false
    $withOperator: Boolean = false
  ) {
    epoch: epochById(id: $epochId) {
      ...Epoch
      ...EpochParticipation
    }
  }
`);

export const getAuthorityMembershipsForEpochQuery = gql(/* GraphQL */ `
  query getAuthorityMembershipsForEpoch($epochId: Int!, $first: Int, $offset: Int) {
    memberships: allAuthorityMemberships(
      first: $first
      offset: $offset
      condition: { epochId: $epochId }
      orderBy: BID_DESC
    ) {
      pageInfo {
        endCursor
        hasNextPage
        hasPreviousPage
        startCursor
      }
      edges {
        node {
          ...AuthorityMembershipWithValidator
        }
      }
      totalCount
    }
  }
`);

export const getOnlineAuthorityMemberInfo = gql(/* GraphQL */ `
  query getOnlineAuthorityMemberInfo(
    $minEpochStartBlockId: Int!
    $groupBy: [AuthorityHeartbeatGroupBy!]!
  ) {
    epochs: allEpoches(filter: { startBlockId: { greaterThanOrEqualTo: $minEpochStartBlockId } }) {
      nodes {
        epochId: id
        startBlock: blockByStartBlockId {
          timestamp
        }
        endBlock: blockByEndBlockId {
          timestamp
        }
        members: authorityMembershipsByEpochId {
          totalCount
        }
        heartbeats: authorityHeartbeatsByEpochId {
          groupedAggregates(groupBy: $groupBy) {
            periodStartUTC: keys
            distinctCount {
              validators: validatorId
            }
          }
        }
      }
    }
  }
`);

gql(/* GraphQL */ `
  fragment AuthorityMembershipWithEpoch on AuthorityMembership {
    id
    reward
    epoch: epochByEpochId {
      id
      startBlockId
      endBlockId
      bond
      totalBonded
      eventType: accountFundingEventsByEpochId(condition: { accountId: $accountId }) {
        groupedAggregates(groupBy: TYPE) {
          type: keys
          sum {
            amount
          }
        }
      }
    }
  }
`);
export const authorityMembershipWithEpochCastFields = [
  'bond',
  'totalBonded',
  'amount',
  'reward',
] as const;
export type CastAuthorityMembershipWithEpochFragment = TokenCast<
  AuthorityMembershipWithEpochFragment,
  (typeof authorityMembershipWithEpochCastFields)[number]
>;

export const getAuthorityMembershipsForValidator = gql(/* GraphQL */ `
  query getAuthorityMembershipsForValidator(
    $validatorId: Int
    $accountId: Int
    $first: Int
    $offset: Int
  ) {
    memberships: allAuthorityMemberships(
      orderBy: ID_DESC
      first: $first
      offset: $offset
      condition: { validatorId: $validatorId }
    ) {
      pageInfo {
        startCursor
        hasPreviousPage
        hasNextPage
        endCursor
      }
      totalCount
      edges {
        node {
          ...AuthorityMembershipWithEpoch
        }
      }
    }
  }
`);
